import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  DatePicker,
  Tag,
  message,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getInsentiveList } from "../../api/transaksi/Insentive";

export default function Insentive() {
  const [data, setData] = useState();
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [bulan, setBulan] = useState(dayjs());
  const [displayedRows, setDisplayedRows] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);

  let isAdmin = false;
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  if (
    sessiData &&
    Array.isArray(sessiData) &&
    sessiData[0]?.user?.[0]?.rolename
  ) {
    const adminRoles = JSON.parse(process.env.REACT_APP_ROLEADMIN || "[]");
    isAdmin = adminRoles.includes(sessiData[0].user[0].rolename);
  }

  useEffect(() => {
    fetchData();
  }, [bulan]);

  const handleExportExcel = () => {
    const exportSource =
      displayedRows.length > 0
        ? displayedRows
        : searchText
        ? filteredData
        : data;

    if (!exportSource || exportSource.length === 0) {
      message.warning("Tidak ada data untuk diekspor.");
      return;
    }

    const exportData = exportSource.map((item) => {
      const row = {};

      if (isAdmin) {
        row["Nama Karyawan"] = item.nama_karyawan || "-";
      }

      row["Periode"] = item.ins_periode
        ? dayjs(item.ins_periode).format("YYYY-MM")
        : "-";

      row["Biaya Insentive"] = item.ins_jumlah || 0;
      row["Keterangan"] = item.ins_keterangan || "-";
      row["Status Pembayaran"] = item.ins_status_pembayaran || "-";
      row["Tanggal dibayar"] = item.ins_tanggal_dibayar
        ? dayjs(item.ins_tanggal_dibayar).format("YYYY-MM-DD")
        : "-";

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan Insentive");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Laporan_Insentive_${bulan?.format("YYYY-MM") || "Semua"}.xlsx`
    );
  };

  const columns = [
    ...(isAdmin
      ? [
          {
            title: "Nama Karyawan",
            dataIndex: "nama_karyawan",
            key: "nama_karyawan",
            filterDropdown: ({
              setSelectedKeys,
              selectedKeys,
              confirm,
              clearFilters,
            }) => (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder="Cari nama"
                  value={selectedKeys[0]}
                  onChange={(e) =>
                    setSelectedKeys(e.target.value ? [e.target.value] : [])
                  }
                  onPressEnter={confirm}
                  style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={confirm}
                    size="small"
                  >
                    Cari
                  </Button>
                  <Button
                    onClick={() => {
                      clearFilters();
                      confirm();
                    }}
                    size="small"
                  >
                    Reset
                  </Button>
                </Space>
              </div>
            ),
            onFilter: (value, record) =>
              record.nama_karyawan?.toLowerCase().includes(value.toLowerCase()),
          },
        ]
      : []),
    {
      title: "Periode",
      dataIndex: "ins_periode",
      key: "ins_periode",
      sorter: (a, b) => a.ins_periode.localeCompare(b.ins_periode),
      render: (ins_periode) =>
        ins_periode && dayjs(ins_periode).isValid()
          ? dayjs(ins_periode).locale("id").format("MMMM YYYY")
          : "-",
    },
    {
      title: "Jumlah",
      dataIndex: "ins_jumlah",
      key: "ins_jumlah",
      render: (val) => `Rp ${Number(val).toLocaleString("id-ID")}`,
      sorter: (a, b) => Number(a.ins_jumlah) - Number(b.ins_jumlah),
    },
    {
      title: "Keterangan",
      dataIndex: "ins_keterangan",
      key: "ins_keterangan",
    },

    {
      title: "Status Pembayaran",
      dataIndex: "ins_status_pembayaran",
      filters: [
        { text: "Tidak Dibayar", value: "tidak dibayar" },
        { text: "Lunas", value: "lunas" },
        { text: "Pending", value: "pending" },
      ],
      onFilter: (value, record) => record.ins_status_pembayaran === value,
      render: (status) => {
        if (!status || status === "pending") return "-";
        const colorMap = {
          pending: "volcano",
          lunas: "blue",
          "tidak dibayar": "gray",
        };
        const labelMap = {
          pending: "Pending",
          lunas: "Lunas",
          "tidak dibayar": "Tidak Dibayar",
        };

        return (
          <Tag color={colorMap[status]}>{labelMap[status]?.toUpperCase()}</Tag>
        );
      },
    },

    {
      title: "Tanggal Pembayaran",
      dataIndex: "ins_tanggal_dibayar",
      sorter: (a, b) =>
        dayjs(a.ins_tanggal_dibayar).unix() -
        dayjs(b.ins_tanggal_dibayar).unix(),
      sortDirections: ["descend", "ascend"],
      render: (ins_tanggal_dibayar) =>
        ins_tanggal_dibayar
          ? dayjs(ins_tanggal_dibayar).format("DD/MM/YYYY")
          : "-",
    },
  ];

  const fetchData = async () => {
    setLoadingTable(true);
    try {
      const result = await getInsentiveList(
        isAdmin ? "" : sessiData[0].user[0].employeeId,
        bulan?.format("YYYY-MM")
      );
      if (result.data.length > 0) {
        setData(result.data);
      } else {
        setData([]);
        message.error("Data tidak ditemukan");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan saat mengambil data";
      message.error(errorMessage);
      setData([]);
    } finally {
      setLoadingTable(false);
    }
  };

  return (
    <div>
      <Divider>Laporan Insentive / Bonus Karyawan</Divider>
      <Space style={{ marginBottom: 16 }}>
        <label>Bulan:</label>
        <DatePicker
          picker="month"
          value={bulan}
          onChange={(val) => setBulan(val)}
        />

        <Button
          icon={<FileExcelOutlined />}
          type="primary"
          onClick={handleExportExcel}
        >
          Export Excel
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={searchText ? filteredData : data}
        rowKey="ovt_id_lembur"
        loading={loadingTable}
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
        onChange={(_, __, ___, extra) => {
          setDisplayedRows(extra.currentDataSource);
        }}
      />
    </div>
  );
}
